/**
 * Chocolate Builder – total price, production days, add to cart (multiple line items).
 * Requires section wrapper #ChocolateBuilder-{{ section.id }} with data attributes.
 */
(function () {
  const ROOT_SEL = '[id^="ChocolateBuilder-"]';
  const PRICE_FACTOR = 100; // Shopify stores prices in cents

  function getRoot() {
    return document.querySelector(ROOT_SEL);
  }

  function getConfig(el) {
    const r = el || getRoot();
    if (!r) return null;
    return {
      sectionId: r.dataset.sectionId,
      cutoffHour: parseInt(r.dataset.cutoffHour, 10) || 16,
      cutoffMinute: parseInt(r.dataset.cutoffMinute, 10) || 0,
      readyLabel: r.dataset.readyLabel || 'Ready in %d days',
      addToCartLabel: r.dataset.addToCartLabel || 'Add to cart',
      baseProductId: r.dataset.baseProductId,
      addonsProductId: r.dataset.addonsProductId,
      afterAdd: r.dataset.afterAdd || 'drawer',
      cartDrawerSection: r.dataset.cartDrawerSection || '',
      cartDrawerOpenSelector: r.dataset.cartDrawerOpenSelector || '.js-drawer-open-right-link, [href="/cart"], .cart-drawer-open'
    };
  }

  function parsePrice(priceCents) {
    return (parseInt(priceCents, 10) || 0) / PRICE_FACTOR;
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(cents / PRICE_FACTOR);
  }

  function getBaseVariantId(root) {
    const sel = root.querySelector('[name="base_variant_id"]');
    return sel ? sel.value : null;
  }

  function getBasePrice(root) {
    const sel = root.querySelector('[name="base_variant_id"]');
    if (!sel) return 0;
    const opt = sel.options[sel.selectedIndex];
    return opt ? parsePrice(opt.dataset.price) : 0;
  }

  function getSelectedIngredients(root) {
    const cards = root.querySelectorAll('[data-ingredient]');
    const out = [];
    cards.forEach(function (card) {
      const cb = card.querySelector('input[name="ingredient"]:checked');
      if (cb && card.dataset.available !== 'false') {
        out.push({
          variantId: card.dataset.variantId,
          price: parsePrice(card.dataset.price),
          productionDays: parseInt(card.dataset.productionDays, 10) || 0
        });
      }
    });
    return out;
  }

  function updateTotal(root) {
    const baseCents = Math.round(getBasePrice(root) * PRICE_FACTOR);
    const ingredients = getSelectedIngredients(root);
    let addonsCents = 0;
    let maxDays = 0;
    ingredients.forEach(function (ing) {
      addonsCents += Math.round(ing.price * PRICE_FACTOR);
      if (ing.productionDays > maxDays) maxDays = ing.productionDays;
    });
    const totalCents = baseCents + addonsCents;

    const basePriceEl = root.querySelector('[data-base-price]');
    if (basePriceEl) basePriceEl.textContent = formatMoney(baseCents);

    const totalPriceEl = root.querySelector('[data-total-price]');
    if (totalPriceEl) totalPriceEl.textContent = formatMoney(totalCents);

    const readyEl = root.querySelector('[data-ready-text]');
    if (readyEl) {
      const cfg = getConfig(root);
      let days = maxDays;
      if (cfg && days >= 0) {
        const now = new Date();
        if (now.getHours() > cfg.cutoffHour || (now.getHours() === cfg.cutoffHour && now.getMinutes() >= cfg.cutoffMinute)) {
          days += 1;
        }
      }
      const label = (cfg && cfg.readyLabel) ? cfg.readyLabel.replace(/%d/g, String(days)) : 'Ready in ' + days + ' days';
      readyEl.textContent = label;
    }

    const btn = root.querySelector('[data-add-to-cart]');
    if (btn) btn.disabled = false;
  }

  function openCartDrawerAndRefresh(root, cfg) {
    var openSel = cfg.cartDrawerOpenSelector;
    var sectionId = (cfg.cartDrawerSection || '').trim();
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (openSel) {
      var openBtn = document.querySelector(openSel);
      if (openBtn) openBtn.click();
    }
    if (sectionId) {
      var base = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      fetch(base + '/?sections=' + encodeURIComponent(sectionId))
        .then(function (r) { return r.json(); })
        .then(function (payload) {
          var html = payload && payload.sections && payload.sections[sectionId];
          if (!html) return;
          var sectionEl = document.getElementById('shopify-section-' + sectionId);
          if (sectionEl) sectionEl.innerHTML = html;
        })
        .catch(function () {});
    }
  }

  function addToCart(root) {
    const baseId = getBaseVariantId(root);
    const ingredients = getSelectedIngredients(root);
    if (!baseId) return;

    const items = [
      { id: baseId, quantity: 1 }
    ];
    ingredients.forEach(function (ing) {
      items.push({ id: ing.variantId, quantity: 1 });
    });

    const btn = root.querySelector('[data-add-to-cart]');
    const errEl = root.querySelector('[data-error]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }
    if (errEl) {
      errEl.classList.add('hidden');
      errEl.textContent = '';
    }

    fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          var cfg = getConfig(root);
          if (btn) {
            btn.disabled = false;
            btn.textContent = root.dataset.addToCartLabel || 'Add to cart';
          }
          if (cfg.afterAdd === 'drawer') {
            openCartDrawerAndRefresh(root, cfg);
          } else {
            if (window.location.pathname.indexOf('/cart') !== 0) {
              window.location.href = (window.Shopify?.routes?.root || '/') + 'cart';
            }
          }
        });
      })
      .catch(function (err) {
        if (btn) {
          btn.disabled = false;
          btn.textContent = root.dataset.addToCartLabel || 'Add to cart';
        }
        if (errEl) {
          errEl.textContent = err.message || 'Something went wrong. Try again.';
          errEl.classList.remove('hidden');
        }
      });
  }

  function bind(root) {
    if (!root) return;

    const baseSelect = root.querySelector('[name="base_variant_id"]');
    if (baseSelect) {
      baseSelect.addEventListener('change', function () {
        updateTotal(root);
      });
    }

    root.querySelectorAll('input[name="ingredient"]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        updateTotal(root);
      });
    });

    const btn = root.querySelector('[data-add-to-cart]');
    if (btn) {
      btn.addEventListener('click', function () {
        addToCart(root);
      });
    }

    updateTotal(root);
  }

  function init() {
    const root = getRoot();
    if (root) bind(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when section is selected
  document.addEventListener('shopify:section:load', function (e) {
    const sectionEl = e.target.querySelector(ROOT_SEL);
    if (sectionEl) bind(sectionEl);
  });
})();
